import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta, SummaryStripBase, Card } from "@/design/primitives";
import { buildSummary, SUMMARY_SEPARATOR } from "@/lib/buildSummary";
import { WatchTile } from "./WatchTile";
import { TileDetail } from "./TileDetail";

export const AsOfStamp = ({ asOf, fresh }) => (
  <MonoMeta as="p" data-testid="as-of-stamp" data-fresh={fresh} className={cn("shrink-0 whitespace-nowrap pt-1", fresh ? "text-os-ink" : "text-os-amber")}>
    as of {asOf}
  </MonoMeta>
);

const StalenessBanner = ({ clock }) => {
  if (clock.fresh) {
    return (
      <MonoMeta as="p" data-testid="breakage-as-of" className="shrink-0 whitespace-nowrap pt-1 text-os-ink">
        as of {clock.stampText || clock.asOf}
      </MonoMeta>
    );
  }
  return (
    <Card
      as="section"
      data-testid="breakage-stale-banner"
      data-dead={clock.dead}
      aria-label="Board freshness warning"
      className="px-5 py-3"
    >
      <p className={cn("text-[0.875rem] leading-[1.45]", clock.dead ? "text-os-red" : "text-os-amber")}>
        {clock.dead
          ? "Board is not checking right now — Kit needs to look at the updater"
          : `Board info is from ${clock.stampText || clock.asOf} — Kit's check-ins keep it current`}
      </p>
    </Card>
  );
};

export const SummaryStrip = ({ tiles, variant, clock }) => {
  const summary = useMemo(() => buildSummary(tiles), [tiles]);
  return (
    <SummaryStripBase
      {...summary}
      separator={SUMMARY_SEPARATOR}
      size={variant === "desktop" ? "lg" : "sm"}
      meta={clock && <StalenessBanner clock={clock} />}
    />
  );
};

// variant: "mobile" (single column, bottom sheet) | "desktop" (3×2 grid, side panel). clock: { asOf, fresh }
export const BreakageBoard = ({ tiles, clock, variant = "desktop", initialOpenSlug = null, renderTileExtra }) => {
  const [openSlug, setOpenSlug] = useState(initialOpenSlug);
  const desktop = variant === "desktop";
  const openItem = tiles.find((t) => t.slug === openSlug);
  const close = useCallback(() => setOpenSlug(null), []);

  return (
    <div data-testid="breakage-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <SummaryStrip tiles={tiles} variant={variant} clock={clock} />
        <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
          {tiles.map((tile) => (
            <div key={tile.slug} className="flex flex-col">
              <WatchTile tile={tile} onOpen={setOpenSlug} />
              {renderTileExtra?.(tile)}
            </div>
          ))}
        </div>
      </div>
      {openItem && <TileDetail item={openItem} variant={variant} onClose={close} />}
    </div>
  );
};
